import { Vec3, add } from '../core/math/vec3'
import { CelestialBody } from '../core/world/body'
import { bodyById } from '../core/world/system'
import { RigidState } from '../core/dynamics/rigidBody'
import { altitudeOf } from '../core/dynamics/flight'
import { OrbitElements, elementsFromState, stateFromElements } from '../core/orbit/elements'
import { propagate } from '../core/orbit/kepler'
import { soiCorrente, bodyPositionRelative, bodyVelocityRelative } from '../core/orbit/ephemeris'
import { SimVessel } from './simulation'

/**
 * Un veicolo "su rotaia" non viene piu integrato: la sua posizione si calcola
 * con Keplero. Nessuna deriva numerica, e il costo non cresce col fattore di
 * accelerazione del tempo — che e l'unico modo di far passare mesi in pochi
 * secondi senza che l'orbita si sfaldi.
 */
export interface OnRails {
  readonly elements: OrbitElements
  readonly bodyId: string
}

export function suRotaia(state: RigidState, body: CelestialBody, t: number): OnRails {
  return { elements: elementsFromState(state.position, state.velocity, body.mu, t), bodyId: body.id }
}

export function statoDaRotaia(rails: OnRails, precedente: RigidState): RigidState {
  const s = stateFromElements(rails.elements)
  return {
    position: s.position,
    velocity: s.velocity,
    orientation: precedente.orientation,
    angularVelocity: precedente.angularVelocity,
  }
}

export const avanzaSuRotaia = (rails: OnRails, dt: number): OnRails =>
  ({ ...rails, elements: propagate(rails.elements, dt) })

/**
 * Un veicolo puo stare su rotaia solo se nessuna forza oltre alla gravita lo
 * sta toccando: motori spenti, fuori dall'atmosfera, non appoggiato a terra.
 */
export function puoStareSuRotaia(sv: SimVessel, body: CelestialBody): boolean {
  if (sv.landed) return false
  if (!sv.debris && sv.activeEngines.length > 0 && sv.controls.throttle > 0) return false
  const quota = altitudeOf(sv.state.position, body)
  const tetto = body.atmosphere?.ceiling ?? 0
  return quota > tetto
}

/**
 * Se il veicolo ha attraversato il confine di una sfera d'influenza, riscrive
 * la sua orbita nel nuovo riferimento.
 *
 * Il punto delicato: posizione e velocita ASSOLUTE non devono cambiare. Si
 * cambia solo il sistema in cui le si racconta, sommando la posizione
 * relativa fra i due corpi.
 */
export function cambioSfera(rails: OnRails, t: number): OnRails | null {
  const attuale = bodyById(rails.bodyId)
  const s = stateFromElements(rails.elements)
  const nuovo = soiCorrente(s.position, attuale, t)
  if (nuovo.id === attuale.id) return null

  const position: Vec3 = add(s.position, bodyPositionRelative(attuale.id, nuovo.id, t))
  const velocity: Vec3 = add(s.velocity, bodyVelocityRelative(attuale.id, nuovo.id, t))
  return { elements: elementsFromState(position, velocity, nuovo.mu, t), bodyId: nuovo.id }
}
